import { useState, type FormEvent } from "react";

const FORM_ID = "dah9a6bp14a1h3mr6g10";
const FORM_URL = "https://readdy.ai/api/form/dah9a6bp14a1h3mr6g10";
const DETAIL_LIMIT = 500;

const categoryOptions = [
  "内部文件与合同",
  "财务与资金流向",
  "利益输送与不当获利",
  "数据造假与违规操作",
  "职场不公与内部人证",
  "环境与公共安全",
  "其他（请在详情中说明）",
];

const materialOptions = [
  "有完整原始文件",
  "有部分文件或截图",
  "只有口头知悉",
  "暂时不方便提供",
];

const expectationOptions = [
  "希望被核实并公开",
  "先私下沟通，再决定",
  "只做情况告知，不要求公开",
  "需要尽快处理（涉及安全）",
];

export default function TipForm() {
  const [detailLength, setDetailLength] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const raw = new FormData(formEl);

    const guard = String(raw.get("website_alt") || "").trim();
    if (guard) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      setDetailLength(0);
      return;
    }

    const payload = new URLSearchParams();
    raw.forEach((value, key) => {
      if (key === "website_alt") {
        return;
      }
      const text = String(value).trim();
      if (text) {
        payload.append(key, text);
      }
    });

    setSubmitting(true);
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const looksSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !looksSpam;

      if (!ok) {
        setStatus("error");
        setFormError(String(serverMsg) || "提交未成功，请检查必填项后重试。");
        return;
      }

      setStatus("success");
      formEl.reset();
      setDetailLength(0);
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交。请稍后重试，或改用加密邮箱投递。");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200";
  const labelClass = "mb-2 block text-sm font-medium text-foreground-800";

  return (
    <form
      id="tip-form"
      data-readdy-form="tip-form"
      onSubmit={handleSubmit}
      className="rounded-xl border border-background-200 bg-background-100 p-6 md:p-9"
      noValidate
    >
      <div className="flex items-center gap-3 border-b border-background-200 pb-6">
        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
          <i className="ri-lock-2-line text-lg"></i>
        </span>
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground-950">线索投递表</h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            仅核实小组可读 · 提交不做 IP 留痕 · 支持全程匿名
          </p>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="subject">
            线索标题 <span className="text-primary-500">*</span>
          </label>
          <input
            id="subject"
            name="subject"
            type="text"
            required
            maxLength={80}
            placeholder="用一句话概括这条线索"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="category">
            线索类型 <span className="text-primary-500">*</span>
          </label>
          <select id="category" name="category" required defaultValue="" className={`${inputClass} cursor-pointer`}>
            <option value="" disabled>
              请选择最接近的一类
            </option>
            {categoryOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="code_name">
            你的称呼或代号
          </label>
          <input
            id="code_name"
            name="code_name"
            type="text"
            maxLength={40}
            placeholder="可以留空，或使用任意代号"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="email">
            联系邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            maxLength={120}
            placeholder="建议使用与真实身份无关的邮箱"
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="detail">
            线索详情 <span className="text-primary-500">*</span>
          </label>
          <textarea
            id="detail"
            name="detail"
            required
            maxLength={DETAIL_LIMIT}
            rows={7}
            onChange={(e) => setDetailLength(e.target.value.length)}
            placeholder="请尽量写清时间、地点、涉及对象、你如何得知，以及你手上有什么材料。也可以只写「先沟通」。"
            className={`${inputClass} resize-y leading-7`}
          ></textarea>
          <p className="mt-2 text-right text-xs text-foreground-500">
            {detailLength} / {DETAIL_LIMIT}
          </p>
        </div>

        <div>
          <label className={labelClass} htmlFor="materials">
            你目前掌握的材料
          </label>
          <select id="materials" name="materials" defaultValue="" className={`${inputClass} cursor-pointer`}>
            <option value="" disabled>
              请选择
            </option>
            {materialOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor="expectation">
            你希望的呈现方式
          </label>
          <select id="expectation" name="expectation" defaultValue="" className={`${inputClass} cursor-pointer`}>
            <option value="" disabled>
              请选择
            </option>
            {expectationOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="field-cloak" aria-hidden="true">
          <label htmlFor="website_alt">网站</label>
          <input
            id="website_alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>
      </div>

      <div className="mt-7 flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-4">
        <i className="ri-information-line mt-0.5 text-base text-secondary-700"></i>
        <p className="text-xs leading-6 text-secondary-900">
          提交即表示你理解：线索需要经过核实，我们不承诺一定公开；在发布之前，你有权随时要求停止处理并删除材料。
        </p>
      </div>

      {status === "success" && (
        <div className="mt-6 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-100 p-4">
          <i className="ri-checkbox-circle-line mt-0.5 text-base text-secondary-800"></i>
          <p className="text-sm leading-6 text-secondary-900">
            线索已进入加密队列。若你填写了联系邮箱，核实小组会在 72 小时内与你联系；若未收到回应，可改用页末的加密邮箱再次投递。
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="mt-6 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 p-4">
          <i className="ri-error-warning-line mt-0.5 text-base text-primary-600"></i>
          <p className="text-sm leading-6 text-primary-800">
            {formError || "提交未成功，请稍后重试，或改用加密邮箱投递。"}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-7 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base"></i>
            正在加密提交…
          </>
        ) : (
          <>
            <i className="ri-shield-keyhole-line text-base"></i>
            提交线索
          </>
        )}
      </button>
    </form>
  );
}